(() => {
  if (customElements.get('ms-installations-scroll')) return;
  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));
  const smooth = value => { const t = clamp(value); return t * t * (3 - 2 * t); };

  class InstallationsScroll extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.msi-card'));
      this.stage = this.querySelector('.msi-stage');
      this.intro = this.querySelector('.msi-intro');
      this.outro = this.querySelector('.msi-outro');
      this.note = this.querySelector('.msi-scroll-note');
      this.motion = matchMedia('(prefers-reduced-motion: reduce)');
      this.queue = () => {
        if (!this.frame && this.animated) this.frame = requestAnimationFrame(() => { this.frame = 0; this.render(); });
      };
      this.resize = () => {
        const enabled = !this.motion.matches && this.cards.length >= 4 && window.innerHeight >= 500;
        if (enabled !== this.animated) this.configure();
        else { this.measure(); this.queue(); }
      };
      this.preference = () => this.configure();
      this.visibility = () => {
        if (document.hidden) { cancelAnimationFrame(this.frame); this.frame = 0; }
        else this.queue();
      };
      this.motion.addEventListener('change', this.preference);
      window.addEventListener('resize', this.resize, { passive: true });
      window.addEventListener('load', this.resize);
      document.addEventListener('visibilitychange', this.visibility);
      this.configure();
      this.layoutObserver = new ResizeObserver(this.resize);
      this.layoutObserver.observe(document.body);
    }

    configure() {
      this.observer?.disconnect();
      window.removeEventListener('scroll', this.queue);
      cancelAnimationFrame(this.frame);
      this.frame = 0;
      this.animated = !this.motion.matches && this.cards.length >= 4 && window.innerHeight >= 500;
      this.classList.toggle('is-animated', this.animated);
      this.removeAttribute('data-pin');
      this.removeAttribute('data-active');
      this.cards.forEach(card => card.removeAttribute('style'));
      this.intro.removeAttribute('style');
      this.intro.removeAttribute('aria-hidden');
      this.outro.removeAttribute('style');
      this.outro.inert = false;
      this.outro.removeAttribute('aria-hidden');
      this.note.removeAttribute('style');
      if (!this.animated) return;
      this.measure();
      this.render();
      this.observer = new IntersectionObserver(entries => {
        const active = entries.some(entry => entry.isIntersecting);
        this.dataset.active = String(active);
        if (active) {
          window.addEventListener('scroll', this.queue, { passive: true });
          this.queue();
        } else {
          window.removeEventListener('scroll', this.queue);
          this.queue();
        }
      }, { rootMargin: '100% 0px' });
      this.observer.observe(this);
    }

    measure() {
      if (!this.animated) return;
      this.width = this.clientWidth;
      this.height = this.stage.offsetHeight;
      this.cardSize = this.cards[0].offsetWidth;
      this.mobile = this.width < 750;
    }

    render() {
      if (!this.animated || document.hidden) return;
      const rect = this.getBoundingClientRect();
      const distance = Math.max(1, rect.height - this.height);
      const progress = clamp(-rect.top / distance);
      this.dataset.pin = rect.top > 0 ? 'before' : rect.bottom <= this.height ? 'after' : 'fixed';
      const reveal = smooth((progress - .86) / .1);
      const intro = 1 - smooth((progress - .28) / .15);
      this.intro.style.opacity = intro.toFixed(3);
      this.intro.setAttribute('aria-hidden', String(intro < .05));
      this.outro.style.opacity = reveal.toFixed(3);
      this.outro.style.visibility = reveal > .01 ? 'visible' : 'hidden';
      this.outro.inert = reveal < .95;
      this.outro.setAttribute('aria-hidden', String(reveal < .95));
      this.note.style.opacity = (1 - reveal).toFixed(3);
      this.style.setProperty('--msi-progress', progress.toFixed(3));
      const radius = 6.1;
      const cameraDistance = 12.5;
      const focalLength = this.height / (2 * Math.tan((this.mobile ? 64 : 44) * Math.PI / 360));
      const cardWidth = 2.5;
      const spacing = (cardWidth + .3) / radius;
      const lastOffset = (this.cards.length - 1) * spacing;
      const travel = 1.5 + clamp(progress / .9) * (lastOffset + 7);
      const lift = smooth((progress - .76) / .18) * 4;
      this.cards.forEach((card, i) => {
        const position = travel - i * spacing;
        const angle = -Math.PI / 2 - position;
        const depth = Math.cos(angle);
        const projection = focalLength / (cameraDistance - depth * radius);
        const x = Math.sin(angle) * radius * projection;
        const y = -(-7 + position * 1.45 + lift) * projection;
        const scale = cardWidth * projection / this.cardSize;
        const yaw = Math.atan2(Math.sin(angle), Math.abs(depth)) * 180 / Math.PI;
        const bank = depth * 13;
        const opacity = smooth(position / .35) * (1 - smooth((progress - .88) / .06));
        card.style.transform = `translate3d(${x.toFixed(2)}px, ${y.toFixed(2)}px, 0) perspective(1000px) rotateZ(${bank.toFixed(2)}deg) rotateY(${yaw.toFixed(2)}deg) scale(${scale.toFixed(4)})`;
        card.style.opacity = opacity.toFixed(3);
        card.style.visibility = opacity > .001 ? 'visible' : 'hidden';
        card.style.zIndex = String(Math.round(depth * 10 + 20));
      });
    }

    disconnectedCallback() {
      this.observer?.disconnect();
      this.layoutObserver?.disconnect();
      cancelAnimationFrame(this.frame);
      window.removeEventListener('scroll', this.queue);
      window.removeEventListener('resize', this.resize);
      window.removeEventListener('load', this.resize);
      document.removeEventListener('visibilitychange', this.visibility);
      this.motion?.removeEventListener('change', this.preference);
    }
  }

  customElements.define('ms-installations-scroll', InstallationsScroll);
})();

